import { useGetCandidateResumes } from '@entities/candidates';
import { Alert } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { CandidateResumeListFilters } from './components/CandidateResumeList.filters';
import { CandidateResumeListTable } from './components/CandidateResumeList.table';
import {
  DEFAULT_RESUME_APPLIED_FILTERS,
  DEFAULT_RESUME_PAGE,
  DEFAULT_RESUME_PAGE_SIZE,
} from './constants';
import type {
  CandidateResumeFilterFormValues,
  CandidateResumeSort,
  CandidateResumeSortField,
} from './types';
import {
  getCandidateResumeAppliedFilters,
  hasCandidateResumeAppliedFilters,
} from './utils/candidateResumeFilters';
import { getCandidateResumeListRequest } from './utils/getCandidateResumeListRequest';

type CandidateResumeListProps = {
  candidateId: number;
};

export const CandidateResumeList = ({
  candidateId,
}: CandidateResumeListProps) => {
  const [filters, setFilters] = useState(DEFAULT_RESUME_APPLIED_FILTERS);
  const [sort, setSort] = useState<CandidateResumeSort>(null);
  const [page, setPage] = useState(DEFAULT_RESUME_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_RESUME_PAGE_SIZE);
  const request = useMemo(
    () => getCandidateResumeListRequest({ filters, sort, page, pageSize }),
    [filters, page, pageSize, sort]
  );
  const resumesQuery = useGetCandidateResumes(candidateId, request);

  const handleApply = (values: CandidateResumeFilterFormValues) => {
    setFilters(getCandidateResumeAppliedFilters(values));
    setPage(DEFAULT_RESUME_PAGE);
  };

  const handleReset = () => {
    setFilters(DEFAULT_RESUME_APPLIED_FILTERS);
    setSort(null);
    setPage(DEFAULT_RESUME_PAGE);
    setPageSize(DEFAULT_RESUME_PAGE_SIZE);
  };

  const handleSortChange = useCallback(
    (field: CandidateResumeSortField, direction?: 'asc' | 'desc') => {
      setSort(direction ? { field, direction } : null);
      setPage(DEFAULT_RESUME_PAGE);
    },
    []
  );

  if (resumesQuery.isError) {
    return <Alert color='red'>Не удалось загрузить профили кандидата</Alert>;
  }

  return (
    <>
      <CandidateResumeListFilters onApply={handleApply} onReset={handleReset} />
      <CandidateResumeListTable
        candidateId={candidateId}
        records={resumesQuery.data?.data ?? []}
        totalRecords={resumesQuery.data?.pagination.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        isLoading={resumesQuery.isLoading || resumesQuery.isFetching}
        hasAppliedFilters={hasCandidateResumeAppliedFilters(filters)}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortChange={handleSortChange}
      />
    </>
  );
};
